<template>
    <div class="d-flex align-center">
        <app-query-selector
            v-if="active.investor"
            ref="investor"
            class="ml-2"
            query-key="investor_id"
            :label="$t('switch_client')"
            :collection="new InvestorCollection()"
            :filters="filters.investor"
            load-object
            item-tooltip="gi_ref"
            style="max-width: 12rem"
            @load="onInvestorLoad"
            @change="onInvestorChange"
        />
        <app-query-selector
            v-if="active.adviser"
            ref="adviser"
            class="ml-2"
            query-key="adviser_id"
            :label="$t('switch_adviser')"
            :collection="new AdviserCollection()"
            :filters="filters.adviser"
            load-object
            item-tooltip="gi_ref"
            style="max-width: 12rem"
            @load="onAdviserLoad"
            @change="onAdviserChange"
        />
        <app-query-selector
            v-if="active.account"
            ref="account"
            class="ml-2"
            query-key="account_id"
            :label="$t('switch_account')"
            :collection="new AccountCollection()"
            :filters="filters.account"
            load-object
            item-tooltip="gi_ref"
            :item-text="
                item => {
                    let text = item.name;

                    if (item.type) {
                        text += ` - ${$t(`enums.account_type.${item.type}`)}`;
                    }

                    return text;
                }
            "
            style="max-width: 12rem"
            :disabled="disabled.account"
            :tooltip="disabled.account ? 'Select a client first' : null"
            @load="onAccountLoad"
            @change="onAccountChange"
        />
    </div>
</template>

<script>
import { Investor, InvestorCollection, Adviser, AdviserCollection, Account, AccountCollection } from '@/models';

export default {
    name: 'InvestorAdviserAccountSelector',
    props: {
        investorFilters: {
            type: Object,
            default: () => ({})
        },
        adviserFilters: {
            type: Object,
            default: () => ({})
        },
        accountFilters: {
            type: Object,
            default: () => ({})
        }
    },
    data: () => ({
        Investor,
        investor: null,
        InvestorCollection,
        //
        Adviser,
        adviser: null,
        AdviserCollection,
        //
        Account,
        account: null,
        AccountCollection,
        //
        active: {
            investor: false,
            adviser: false,
            account: false
        },
        //
        filters: {
            investor: null,
            adviser: null,
            account: null
        }
    }),
    computed: {
        disabled() {
            const disabled = {
                investor: false,
                adviser: false,
                account: false
            };

            if (this.active.investor && (!this.investor || !this.investor.id)) {
                disabled.account = true;
            }

            return disabled;
        }
    },
    watch: {
        investorFilters: {
            handler(filters, oldFilters) {
                if (JSON.stringify(filters) !== JSON.stringify(oldFilters)) {
                    this.setFilters();
                }
            },
            deep: true,
            immediate: false
        },
        adviserFilters: {
            handler(filters, oldFilters) {
                if (JSON.stringify(filters) !== JSON.stringify(oldFilters)) {
                    this.setFilters();
                }
            },
            deep: true,
            immediate: false
        },
        accountFilters: {
            handler(filters, oldFilters) {
                if (JSON.stringify(filters) !== JSON.stringify(oldFilters)) {
                    this.setFilters();
                }
            },
            deep: true,
            immediate: false
        }
    },
    methods: {
        setup() {
            if (!this.isInvestor()) {
                this.active.investor = true;
            }

            if (this.isGrowthInvest()) {
                this.active.adviser = true;
            }

            this.active.account = true;
        },
        setFilters() {
            const filters = {
                investor: { ...this.investorFilters },
                adviser: { ...this.adviserFilters },
                account: { ...this.accountFilters }
            };

            if (this.active.investor && this.investor && this.investor.id) {
                filters.adviser.has_investor = this.investor.id;
                filters.account.investor_id = `${this.getFilterOperator('is')}:${this.investor.id}`;
            }

            if (this.active.adviser && this.adviser && this.adviser.id) {
                filters.account.adviser_id = `${this.getFilterOperator('is')}:${this.adviser.id}`;
                filters.investor.has_adviser = this.adviser.id;
            }

            if (this.active.account && this.account && this.account.id) {
                filters.investor.id = `${this.getFilterOperator('is')}:${this.account.investor_id}`;
            }

            this.filters = filters;
        },
        onLoad() {
            if (this.active.investor && !this.investor) {
                return;
            }

            if (this.active.adviser && !this.adviser) {
                return;
            }

            if (this.active.account && !this.account) {
                return;
            }

            this.setFilters();

            this.$emit('load', {
                investor: this.investor,
                adviser: this.adviser,
                account: this.account
            });
        },
        onChange() {
            this.setFilters();

            this.$emit('change', {
                investor: this.investor,
                adviser: this.adviser,
                account: this.account
            });
        },
        //
        onInvestorLoad(investor) {
            this.investor = investor ? new Investor(investor) : new Investor();
            this.onLoad();
        },
        onAdviserLoad(adviser) {
            this.adviser = adviser ? new Adviser(adviser) : new Adviser();
            this.onLoad();
        },
        onAccountLoad(account) {
            this.account = account ? new Account(account) : new Account();
            this.onLoad();
        },
        //
        onInvestorChange(investor) {
            if (!investor) {
                this.account = new Account();
                if (this.$refs.account) {
                    this.$refs.account.clear();
                }
            }

            this.investor = investor ? new Investor(investor) : new Investor();
            this.onChange();
        },
        onAdviserChange(adviser) {
            this.adviser = adviser ? new Adviser(adviser) : new Adviser();
            this.onChange();
        },
        onAccountChange(account) {
            this.account = account ? new Account(account) : new Account();
            this.onChange();
        }
    }
};
</script>
